import React, { useState, useEffect, useMemo } from "react";
import { Form } from "antd";
import InputMask from "react-input-mask";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { getCountries } from "react-phone-number-input/input";
import countryListEn from "react-phone-number-input/locale/en";
import countryListPt from "react-phone-number-input/locale/pt";
import BackArrow from "../../../../newUXAssets/icons/BackArrow";
import Input from "../../../../newUXComponents/Input";
import Select from "../../../../newUXComponents/Select";
import Button from "../../../../newUXComponents/Button";
import TelephoneInput, {
  getFullTelephoneNumber,
  removeCountryFromTelephoneNumber,
  getCountryFromTelephoneNumber,
} from "../../../../newUXComponents/TelephoneInput";
import useNotification from "../../../../hooks/newUXNotification";
import { useLoading } from "../../../../hooks/loading";
import {
  getSingleCollaborator,
  editEmployee,
} from "../../../../redux/modules/employee/actions";
import {
  getBrazilianStates,
  getBrazilianCitiesByStateId,
} from "../../../../service/localities.service";

import { validation, INITIAL_VALUES } from "./validations";
import {
  Container,
  ReturnLink,
  Title,
  ButtonContainer,
  FormContainer,
  CustomExtraLabel,
} from "./styles";

const NewEmployee = () => {
  const intl = useIntl();
  const [documentMask, setDocumentMask] = useState("999.999.999-99");
  const [documentLabel, setDocumentLabel] = useState(
    intl.formatMessage({ id: "document_cpf" })
  );
  const [defaultCountry, setDefaultCountry] = useState('BR');
  const [nationality, setNationality] = useState(false);
  const [documentHasError, setDocumentHasError] = useState(false);
  const [countries, setCountries] = useState([]);
  const [brazilianStates, setBrazilianStates] = useState([]);
  const [brazilianCities, setBrazilianCities] = useState([]);
  const [loadedState, setLoadedState] = useState();
  const [loadedCity, setLoadedCity] = useState();
  const [hasLoadedCity, setHasLoadedCity] = useState(false);
  const isPortuguese = useMemo(() => intl.locale === 'pt', [intl]);

  const [form] = Form.useForm();
  const history = useHistory();
  const dispatch = useDispatch();
  const notification = useNotification();
  const { handleLoading } = useLoading();

  const handleTypeChanges = (value) => {
    setNationality(value);
    setDocumentHasError(false);
    switch (value) {
      case false:
        setDocumentLabel(intl.formatMessage({ id: "document_cpf" }));
        setDocumentMask("999.999.999-99");
        break;
      case true:
        setDocumentLabel(intl.formatMessage({ id: "document_passport" }));
        setDocumentMask("");
        break;
      default:
        setDocumentLabel(intl.formatMessage({ id: "document" }));
        break;
    }
    form.setFieldValue("document", "");
    form.setFieldValue("country", undefined);
    form.setFieldValue("internationalCity", "");
    form.setFieldValue("state", undefined);
    form.setFieldValue("brazilianCity", undefined);
    setBrazilianCities([]);
    form.validateFields(["document"]);
  };

  const handleDocumentChange = (e) => {
    const value = e.target.value;
    
    if (nationality === true) {
      const upperValue = value.toUpperCase();
      form.setFieldValue("document", upperValue);
    } else {
      form.setFieldValue("document", value);
    }

    setTimeout(() => {
      form
        .validateFields(["document"])
        .then(() => {
          setDocumentHasError(false);
        })
        .catch((error) => {
          setDocumentHasError(true);
        });
    }, 300);
  };

  const handleDocumentBlur = () => {
    const value = form.getFieldValue("document");

    if (!value) return;

    form
      .validateFields(["document"])
      .then(() => {
        setDocumentHasError(false);
      })
      .catch((error) => {
        setDocumentHasError(true);
      });
  };

  const callBackError = (message) => {
    handleLoading(false);
    notification.displayError(
      intl.formatMessage({ id: "api.error." + message }),
      "post-edit-employee-error-message"
    );
  };

  const callBackSuccess = (message) => {
    handleLoading(false);
    notification.displaySuccess(
      intl.formatMessage({ id: "success." + message }),
      "post-edit-employee-success-message"
    );
    history.push("/credentials/register");
  };

  const callBackSuccessLoad = (loadedCollaborator) => {
    setNationality(loadedCollaborator.international);
    handleTypeChanges(loadedCollaborator.international);
    form.setFieldsValue({
      nationality: loadedCollaborator.international,
      document: loadedCollaborator.document,
      name: loadedCollaborator.name,
      last_name: loadedCollaborator.lastName,
      credential_name: loadedCollaborator.credentialName,
      email: loadedCollaborator.email,
      assignment: loadedCollaborator.jobTitle,
      country: loadedCollaborator.country || undefined,
      internationalCity: loadedCollaborator.city,
      state: loadedCollaborator.state || undefined,
      brazilianCity: loadedCollaborator.city || undefined,
    });

    if (loadedCollaborator.telephone.length !== 0 && !!loadedCollaborator.telephone) {
      form.setFieldsValue({
        telephone: removeCountryFromTelephoneNumber(loadedCollaborator.telephone),
        telephone_selected_country: getCountryFromTelephoneNumber(
          loadedCollaborator.telephone
        ).value,
      });
      setDefaultCountry(getCountryFromTelephoneNumber(loadedCollaborator.telephone).value)
    } else {
      form.setFieldsValue({
        telephone: "",
        telephone_selected_country: "",
      });
    }

    setLoadedState(loadedCollaborator.state || "[]");
    setLoadedCity(loadedCollaborator.city || "[]");
  }

  useEffect(() => {
    dispatch(
      getSingleCollaborator({
        callBackError,
        callBackSuccess: callBackSuccessLoad,
        id: history.location.state.id
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBrazilianCities = async (stateId) => {
    try {
      handleLoading(true);
      const cities = await getBrazilianCitiesByStateId(stateId);
      setBrazilianCities(cities);
    } catch (error) {
      notification.displayError(
        intl.formatMessage({ id: "error.loading_cities" }),
        "error-edit-employee-loading-cities"
      );
      setBrazilianCities([]);
    } finally {
      handleLoading(false);
    }
  };

  useEffect(() => {
    if (nationality
      || !loadedState
      || brazilianStates.length === 0) return;

    const foundState = brazilianStates.find(
      (state) => state.value === loadedState
    );
    if (foundState) {
      fetchBrazilianCities(foundState.stateId);
    } else {
      form.setFieldValue("state", undefined);
      form.setFieldValue("brazilianCity", undefined);
      setHasLoadedCity(true);
      notification.displayWarning(
        intl.formatMessage(
          { id: "state_not_found" },
          { state: loadedState },
        ),
        "load-employee-state-warning-message"
      );
    }
  }, [loadedState, brazilianStates]);

  useEffect(() => {
    if (nationality
      || !loadedCity
      || brazilianCities.length === 0
      || hasLoadedCity) return;

    const foundCity = brazilianCities.find(
      (city) => city.value === loadedCity
    );
    if (!foundCity) {
      form.setFieldValue("brazilianCity", undefined);
      notification.displayWarning(
        intl.formatMessage(
          { id: "city_not_found" },
          { city: loadedCity },
        ),
        "load-employee-city-warning-message"
      );
    }

    setHasLoadedCity(true);
  }, [loadedCity, brazilianCities]);

  const handleStateChange = (stateValue, option) => {
    form.setFieldValue("brazilianCity", undefined);

    if (!option?.stateId) {
      setBrazilianCities([]);
      return;
    }

    fetchBrazilianCities(option.stateId);
  };

  useEffect(() => {
    const fetchBrazilianStates = async () => {
      try {
        handleLoading(true);
        const states = await getBrazilianStates();
        setBrazilianStates(states);
      } catch (error) {
        notification.displayError(
          intl.formatMessage({ id: "error.loading_states" }),
          "error-edit-employee-loading-states"
        );
        setBrazilianStates([]);
      } finally {
        handleLoading(false);
      }
    };

    const allCountries = getCountries();
    const countryList = isPortuguese ? countryListPt : countryListEn;
    const countriesOptions = allCountries.map((countryCode) => ({
      label: countryList[countryCode],
      value: countryList[countryCode],
    }));
    const sortedCountries = countriesOptions.sort((a, b) =>
      a.label.localeCompare(b.label)
    );
    setCountries(sortedCountries);

    fetchBrazilianStates();
  }, [isPortuguese]);

  const onFinish = (values) => {
    const newEmployee = {
      international: values.nationality,
      name: values.name,
      lastName: values.last_name,
      credentialName: values.credential_name,
      document: values.document,
      jobTitle: values.assignment,
      email: values.email,
      country: values.country || null,
      city: values.nationality
        ? values.internationalCity
        : values.brazilianCity,
      state: values.state,
      telephone: getFullTelephoneNumber(
        values.telephone,
        values.telephone_selected_country.value,
        defaultCountry
      ),
    };
    handleLoading(true);
    dispatch(
      editEmployee({
        employee: newEmployee,
        callBackError,
        callBackSuccess,
        id: history.location.state.id,
      })
    );
  };

  const handleGoBack = () => {
    history.push("/credentials/register");
  };

  const labelWithExtra = (labelKey, extraKey, extraValues = {}) => (
    <span>
      {intl.formatMessage({ id: labelKey })}
      <CustomExtraLabel>
        {intl.formatMessage({ id: extraKey }, extraValues)}
      </CustomExtraLabel>
    </span>
  );

  return (
    <Container>
      <ReturnLink onClick={handleGoBack}>
        <BackArrow />
        <p>{intl.formatMessage({ id: "goback" })}</p>
      </ReturnLink>
      <Title>{intl.formatMessage({ id: "editing_collaborator" })}</Title>
      <FormContainer>
        <Form
          layout="vertical"
          form={form}
          name="edit-employee"
          onFinish={onFinish}
          initialValues={INITIAL_VALUES}
        >
          <Select
            name="nationality"
            label={intl.formatMessage({ id: "nationality" })}
            showArrow
            allowClear
            placeholder={intl.formatMessage({ id: "nationality" })}
            rules={validation("nationality", intl)}
            onChange={(e) => handleTypeChanges(e)}
            options={[
              {
                label: intl.formatMessage({ id: "national" }),
                value: false,
              },
              {
                label: intl.formatMessage({ id: "international" }),
                value: true,
              },
            ]}
          />
          <Input
            type="text"
            name="name"
            label={intl.formatMessage({ id: "name" })}
            placeholder={intl.formatMessage({ id: "name" })}
            rules={validation("name", intl)}
          />
          <Input
            type="text"
            name="last_name"
            label={intl.formatMessage({ id: "last_name" })}
            placeholder={intl.formatMessage({ id: "last_name" })}
            rules={validation("last_name", intl)}
          />
          <Input
            type="text"
            name="credential_name"
            label={labelWithExtra(
              "credential_name",
              "up_to_custom_characters",
              { quantity: 23 }
            )}
            placeholder={intl.formatMessage({ id: "credential_name" })}
            rules={validation("credential_name", intl)}
          />
          <Input
            type="text"
            name="document"
            as={InputMask}
            mask={documentMask}
            placeholder={documentLabel}
            label={documentLabel}
            rules={validation("document", intl, nationality)}
            onChange={handleDocumentChange}
            onBlur={handleDocumentBlur}
            style={{
              borderColor: documentHasError ? '#ff4d4f' : undefined,
              boxShadow: documentHasError ? '0 0 0 2px rgba(255, 77, 79, 0.2)' : undefined
            }}
          />
          <Input
            type="text"
            name="assignment"
            label={labelWithExtra(
              "assignment",
              "up_to_custom_characters",
              { quantity: 50 }
            )}
            placeholder={intl.formatMessage({ id: "assignment" })}
            rules={validation("assignment", intl)}
          />
          <Input
            type="text"
            name="email"
            label={intl.formatMessage({ id: "email" })}
            placeholder={intl.formatMessage({ id: "email" })}
            rules={validation("email", intl)}
          />
          {nationality ? (
            <>
              <Select
                name="country"
                label={intl.formatMessage({ id: "country" })}
                allowClear
                placeholder={intl.formatMessage({
                  id: "select_country_where_live"
                })}
                rules={validation("country", intl, nationality)}
                options={countries}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
              <Input
                type="text"
                name="internationalCity"
                label={intl.formatMessage({ id: "city" })}
                placeholder={intl.formatMessage({
                  id: "type_city_where_live"
                })}
                rules={validation("internationalCity", intl, nationality)}
                maxLength={255}
              />
            </>
          ) : (
            <>
              <Select
                name="state"
                label={intl.formatMessage({ id: "state" })}
                placeholder={intl.formatMessage({
                  id: "select_state_where_live"
                })}
                rules={validation("state", intl, nationality)}
                options={brazilianStates}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleStateChange}
              />
              <Select
                name="brazilianCity"
                label={intl.formatMessage({ id: "city" })}
                placeholder={intl.formatMessage({
                  id: "select_city_where_live"
                })}
                rules={validation("brazilianCity", intl, nationality)}
                options={brazilianCities}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </>
          )}
          <TelephoneInput
            name="telephone"
            placeholder={intl.formatMessage({
              id: "telephone",
            })}
            label={intl.formatMessage({
              id: "telephone",
            })}
            rules={validation("telephone", intl)}
            refForm={form}
            defaultCountryCode={defaultCountry}
          />
          <ButtonContainer>
            <Button
              label={intl.formatMessage({ id: "cancel" })}
              type="link"
              onClick={handleGoBack}
            />
            <Button
              label={intl.formatMessage({ id: "save" })}
              type="primary"
              htmlType="submit"
            />
          </ButtonContainer>
        </Form>
      </FormContainer>
    </Container>
  );
};

export default NewEmployee;
